import {z} from 'zod';
import {TrimSpecificationRequest} from '@artemis-group/aiw-contracts';
import type {HarnessFloor, HarnessTrim} from '../mock/work-record';

export type TrimSpecificationInput = z.infer<typeof TrimSpecificationRequest>;
export type TrimSpecificationPatch = {
  productID: string;
  variantID: string | null;
  specificationStatus: 'confirmed' | 'proposed' | 'unresolved';
  stockLengthMM: number | null;
};
export type TrimSpecificationPreview = {
  types: Array<{
    product_id: string;
    name: string;
    unit: string | null;
    default_variant_id: string | null;
    variants: Array<{variant_id: string; display_label: string; stock_length_mm: number | null}>;
  }>;
  patch: TrimSpecificationPatch | null;
};

const Preview = z.object({
  types: z.array(z.object({
    product_id: z.string().min(1), name: z.string().min(1), unit: z.string().nullable(),
    default_variant_id: z.string().nullable(),
    variants: z.array(z.object({variant_id: z.string().min(1), display_label: z.string().min(1), stock_length_mm: z.number().positive().nullable()})),
  })),
  patch: z.object({
    productID: z.string().min(1), variantID: z.string().min(1).nullable(),
    specificationStatus: z.enum(['confirmed', 'proposed', 'unresolved']), stockLengthMM: z.number().positive().nullable(),
  }).nullable(),
});

export function trimFloorContext(floor: HarnessFloor, trim: HarnessTrim): {floorProductID: string; floorVariantID: null; kind: 'trims' | 'scotia'} | null {
  const area = floor.areas.find(candidate => candidate.id === trim.areaID);
  const productID = area?.newFloor?.product?.productID;
  if (!area || !productID) return null;
  return {floorProductID: productID, floorVariantID: null, kind: trim.category === 'scotia' || trim.type === 'Scotia' ? 'scotia' : 'trims'};
}

/** Identity-only patch: geometry, measured lengths, segments and legacy text are not catalogue output. */
export function applyTrimSpecification(trim: HarnessTrim, patch: TrimSpecificationPatch): HarnessTrim {
  return {...trim, productID: patch.productID, variantID: patch.variantID, specificationStatus: patch.specificationStatus, stockLengthMM: patch.stockLengthMM ?? undefined};
}

export function clearTrimSpecification(trim: HarnessTrim): HarnessTrim {
  const next = {...trim, productID: null, variantID: null, specificationStatus: 'unresolved' as const};
  delete next.stockLengthMM;
  return next;
}

export function trimOrderReady(trim: HarnessTrim): boolean {
  return Boolean(trim.measurementSource && trim.type && trim.productID && trim.variantID && trim.specificationStatus === 'confirmed' && trim.stockLengthMM && trim.stockLengthMM > 0);
}

/** One latest-only gate for room, floor, profile and work-revision changes. */
export function createTrimSpecificationCoordinator(request: (input: TrimSpecificationInput) => Promise<TrimSpecificationPreview>) {
  let generation = 0;
  return {
    async preview(input: TrimSpecificationInput): Promise<TrimSpecificationPreview | null> {
      const own = ++generation;
      try {
        const result = await request(input);
        return own === generation ? result : null;
      } catch (error) {
        if (own !== generation) return null;
        throw error;
      }
    },
    invalidate() { generation += 1; },
  };
}

export async function postTrimSpecification(input: TrimSpecificationInput, signal?: AbortSignal): Promise<TrimSpecificationPreview> {
  // Keep pure reducer tests independent of Vite's import.meta.env runtime.
  const [{authHeaders, fetchImpl}, {env}] = await Promise.all([import('./api'), import('./env')]);
  const response = await fetchImpl(`${env.apiBase}/api/trim-specification/preview`, {
    method: 'POST', headers: {...await authHeaders(), 'content-type': 'application/json'}, body: JSON.stringify(input), cache: 'no-store', signal,
  });
  const body = await response.json() as {data?: unknown; error?: string};
  if (!response.ok) throw new Error(body.error ?? 'Trim specification is unavailable.');
  const parsed = Preview.safeParse(body.data);
  if (!parsed.success) throw new Error('The trim specification response was invalid. The saved trim has not changed.');
  if (new Set(parsed.data.types.map(type => type.product_id)).size !== parsed.data.types.length || parsed.data.types.some(type => new Set(type.variants.map(variant => variant.variant_id)).size !== type.variants.length)) {
    throw new Error('The trim specification response contained ambiguous identities. The saved trim has not changed.');
  }
  return parsed.data;
}
